import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { articles } from "../data/articles";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import SEO from "../components/SEO";

const CardThumb = ({ src, alt }) => (
  <div className="aspect-[16/9] rounded-xl overflow-hidden relative" style={{ backgroundColor: "var(--card-tint)" }}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, transparent 60%, rgba(23,20,18,0.35) 100%)",
      }}
      aria-hidden
    />
  </div>
);

const BlogList = () => {
  return (
    <div data-testid="blog-list-page">
      <SEO
        title="Automation Insights — Zelaflo Labs Blog"
        description="Practical guides on business automation, document triage, and workflow automation for small and mid-size businesses."
        path="/blog"
      />

      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="blog-hero">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Blog</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter max-w-4xl text-balance" style={{ color: "var(--text)" }}>
              Automation <span className="text-[#7B2FBE]">Insights.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Practical guides for small businesses ready to work smarter.
            </p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      <section className="relative py-16 md:py-24" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link
                  to={`/blog/${a.slug}`}
                  className="service-glow group flex flex-col h-full p-5 md:p-6 rounded-2xl border transition-all duration-500 hover:-translate-y-1"
                  style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
                  data-testid={`blog-card-${a.slug}`}
                >
                  <CardThumb src={a.heroImage} alt={a.heroAlt} />
                  <div className="mt-5 flex items-center gap-4 text-xs" style={{ color: "var(--text-subtle)" }}>
                    <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {a.date}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {a.readTime}</span>
                  </div>
                  <h3 className="font-heading text-xl md:text-[1.35rem] font-semibold mt-3 tracking-tight leading-tight" style={{ color: "var(--text)" }}>
                    {a.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed flex-1" style={{ color: "var(--text-muted)" }}>
                    {a.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2DD4BF] group-hover:translate-x-1 transition-transform">
                    Read More <ArrowRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
